'use client'

import { XIcon } from 'lucide-react'
import * as React from 'react'
import { Drawer as DrawerPrimitive } from 'vaul'
import useDeviceDetect from '~/src/hooks/useDeviceDetect'
import { cn } from '~/src/lib/utils'

type DrawerDirection = 'top' | 'bottom' | 'left' | 'right'

/**
 * How many drawers deep we are: 0 outside any drawer, 1 inside a top-level
 * drawer, 2 inside a drawer opened from that one, and so on. React context
 * crosses portals, so a drawer rendered inside another drawer's content sees
 * its parent's level.
 */
const DrawerDepthContext = React.createContext(0)

/** How many drawers deep this render is (0 outside any drawer). */
export const useDrawerDepth = () => React.useContext(DrawerDepthContext)

/** A top-level drawer's overlay and panel stacking (unchanged app-wide). */
export const DRAWER_OVERLAY_Z = 99999
export const DRAWER_PANEL_Z = 999999

/**
 * A drawer opened from inside another drawer stacks its overlay and panel
 * just above its parent's panel, so the parent dims behind it instead of the
 * two panels merging into one white sheet. Depth 1 is a top-level drawer.
 */
export function drawerZIndex(depth: number): {
  overlay: number
  panel: number
} {
  if (depth <= 1) return { overlay: DRAWER_OVERLAY_Z, panel: DRAWER_PANEL_Z }
  return {
    overlay: DRAWER_PANEL_Z + (depth - 1) * 2 - 1,
    panel: DRAWER_PANEL_Z + (depth - 1) * 2,
  }
}

type DrawerRootProps = React.ComponentProps<typeof DrawerPrimitive.Root> & {
  dragEnabled?: boolean
  dragEnabledByDirection?: Partial<Record<DrawerDirection, boolean>>
  /**
   * **Push mode** — shift this element via `transform: translateX` when the
   * drawer opens, so the main content moves aside without compressing its
   * layout width. Works for `direction="right"` or `"left"`.
   *
   * Pair with `<DrawerContent hideOverlay>` so the content behind stays fully
   * visible. Uses `transform` (not padding/margin) so `w-full` containers
   * shift cleanly without reflowing — the same mechanic ChatGPT uses.
   *
   * @example
   * ```tsx
   * const chatRef = useRef<HTMLDivElement>(null)
   *
   * <div ref={chatRef}>chat messages</div>
   *
   * <Drawer direction="right" pushTargetRef={chatRef} pushWidth="440px">
   *   <DrawerContent direction="right" hideOverlay>
   *     side panel content
   *   </DrawerContent>
   * </Drawer>
   * ```
   *
   * When to use: detail panels anchored to the right of a fixed-width content
   * column where you want the main content to remain readable and usable while
   * the panel is open. Not suitable for full-viewport drawers or mobile bottom
   * sheets — use `shouldScaleBackground` for that effect instead.
   */
  pushTargetRef?: React.RefObject<HTMLElement | null>
  /**
   * Panel width used to compute the `translateX` shift on `pushTargetRef`
   * (actual shift = pushWidth × −0.5 for `right`, × +0.5 for `left`).
   * Should match the drawer panel's CSS width. @default "440px"
   */
  pushWidth?: string
}

const Drawer = ({
  shouldScaleBackground = true,
  direction = 'bottom',
  handleOnly,
  dragEnabled,
  dragEnabledByDirection,
  pushTargetRef,
  pushWidth = '440px',
  onOpenChange,
  open,
  ...props
}: DrawerRootProps) => {
  const depth = React.useContext(DrawerDepthContext) + 1
  const { isMobile } = useDeviceDetect()
  const defaultDragEnabled =
    isMobile || (direction !== 'left' && direction !== 'right')
  const directionDragOverride = dragEnabledByDirection?.[direction]
  const explicitDragEnabled =
    directionDragOverride ??
    dragEnabled ??
    (handleOnly != null ? !handleOnly : undefined)
  const shouldEnableDrag = explicitDragEnabled ?? defaultDragEnabled

  // Snapshot the target's pre-push inline `transform` / `transition` so
  // we can restore them on close/unmount. Without this, any inline
  // transform a parent put on the push target (e.g. a Framer Motion
  // layout transform) gets clobbered and never reinstated.
  const originalStylesRef = React.useRef<{
    transform: string
    transition: string
  } | null>(null)

  const applyPush = React.useCallback(
    (isOpen: boolean) => {
      const el = pushTargetRef?.current
      if (!el || direction === 'bottom' || direction === 'top') return
      if (!isOpen) {
        // Restore exactly what was there before we started pushing.
        const snap = originalStylesRef.current
        if (!snap) return
        el.style.transition = snap.transition
        el.style.transform = snap.transform
        originalStylesRef.current = null
        return
      }
      // Capture the pre-push inline styles the first time we touch the
      // element so we have something to restore to on close.
      if (originalStylesRef.current === null) {
        originalStylesRef.current = {
          transform: el.style.transform,
          transition: el.style.transition,
        }
      }
      const snap = originalStylesRef.current
      // Use transform (not padding/margin) so the content block shifts
      // visually without compressing its layout width. Padding/margin on a
      // w-full element squishes the content; transform moves it intact.
      // Shift by half the panel width — keeps the content centered in the
      // remaining viewport area (same mechanic ChatGPT uses).
      el.style.transition = 'transform 300ms cubic-bezier(0.32, 0.72, 0, 1)'
      const shift = `calc(${pushWidth} * -0.5)`
      const pushTransform =
        direction === 'right'
          ? `translateX(${shift})`
          : `translateX(calc(${shift} * -1))`
      el.style.transform = snap.transform
        ? `${snap.transform} ${pushTransform}`
        : pushTransform
    },
    [pushTargetRef, direction, pushWidth]
  )

  // Sync push state with controlled `open` prop
  React.useEffect(() => {
    if (typeof open !== 'undefined') applyPush(open)
  }, [open, applyPush])

  // Restore the target's original inline transform/transition on unmount.
  React.useEffect(() => {
    return () => {
      const el = pushTargetRef?.current
      const snap = originalStylesRef.current
      if (!el || !snap) return
      el.style.transform = snap.transform
      el.style.transition = snap.transition
      originalStylesRef.current = null
    }
  }, [pushTargetRef])

  const handleOpenChange = React.useCallback(
    (isOpen: boolean) => {
      applyPush(isOpen)
      onOpenChange?.(isOpen)
    },
    [applyPush, onOpenChange]
  )

  return (
    <DrawerDepthContext.Provider value={depth}>
      <DrawerPrimitive.Root
        shouldScaleBackground={shouldScaleBackground}
        direction={direction}
        handleOnly={!shouldEnableDrag}
        open={open}
        onOpenChange={handleOpenChange}
        {...props}
      />
    </DrawerDepthContext.Provider>
  )
}
Drawer.displayName = 'Drawer'

const DrawerTrigger = DrawerPrimitive.Trigger as typeof DrawerPrimitive.Trigger

const DrawerPortal = DrawerPrimitive.Portal as typeof DrawerPrimitive.Portal

const DrawerClose = DrawerPrimitive.Close as typeof DrawerPrimitive.Close

const DrawerOverlay: React.ForwardRefExoticComponent<
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Overlay> &
    React.RefAttributes<React.ComponentRef<typeof DrawerPrimitive.Overlay>>
> = React.forwardRef<
  React.ComponentRef<typeof DrawerPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Overlay
    ref={ref}
    className={cn('fixed inset-0 bg-black/70', className)}
    {...props}
  />
))
DrawerOverlay.displayName = DrawerPrimitive.Overlay.displayName

interface DrawerContentProps
  extends React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Content> {
  header?: React.ReactNode
  hideNotch?: boolean
  hideOverlay?: boolean
  showXClose?: boolean
  overlayClassName?: string
  notchClassName?: string
  adjustForSidebar?: boolean
  direction?: 'bottom' | 'right' | 'left' | 'top'
}

const DrawerContent: React.ForwardRefExoticComponent<
  DrawerContentProps &
    React.RefAttributes<React.ComponentRef<typeof DrawerPrimitive.Content>>
> = React.forwardRef<
  React.ComponentRef<typeof DrawerPrimitive.Content>,
  DrawerContentProps
>(
  (
    {
      className,
      children,
      header,
      hideNotch,
      hideOverlay,
      showXClose,
      overlayClassName,
      notchClassName,
      adjustForSidebar,
      direction = 'bottom',
      ...props
    },
    ref
  ) => {
    const depth = React.useContext(DrawerDepthContext)
    const z = drawerZIndex(depth)
    const nested = depth > 1
    return (
      <DrawerPortal>
        {!hideOverlay && (
          <DrawerOverlay
            // A nested drawer's overlay lies on top of the parent's, so a
            // lighter shade keeps the strip above both from going black.
            className={cn(nested && 'bg-black/40', overlayClassName)}
            data-testid='drawer-overlay'
            style={{ zIndex: z.overlay }}
          >
            {header}
          </DrawerOverlay>
        )}
        <DrawerPrimitive.Content
          ref={ref}
          className={cn(
            // `surface-raised` is white in light mode and near-black in dark mode.
            // The DS `bg-ds-bg-primary` token is white in both (it only flips
            // inside `.dupe-maxx-theme`), which left drawers white in dark mode.
            'bg-surface-raised fixed z-50 flex !select-auto flex-col overflow-hidden',
            direction === 'right'
              ? cn(
                  'inset-y-0 right-0 h-full w-[440px]',
                  hideOverlay
                    ? 'border-l border-ds-border-primary'
                    : 'rounded-l-2xl shadow-[-4px_0_24px_rgba(0,0,0,0.06)]'
                )
              : direction === 'left'
                ? cn(
                    'inset-y-0 left-0 h-full w-[440px]',
                    hideOverlay
                      ? 'border-r border-ds-border-primary'
                      : 'rounded-r-2xl shadow-[4px_0_24px_rgba(0,0,0,0.06)]'
                  )
                : 'inset-x-0 bottom-0 h-auto rounded-t-[36px]',
            {
              'md:left-[var(--sidebar-width-collapsed)]':
                adjustForSidebar && direction === 'bottom',
            },
            className
          )}
          style={{ zIndex: z.panel }}
          {...props}
        >
          {children}

          {!hideNotch && (
            <div
              className={cn(
                'fixed inset-x-0 top-0 z-10 flex items-center justify-center bg-inherit p-3.5 md:hidden',
                notchClassName
              )}
            >
              <div className='h-[5px] w-[30px] rounded-full bg-current opacity-20' />
            </div>
          )}

          {showXClose && (
            <DrawerClose
              aria-label='Close'
              className='absolute right-2 top-2 z-50 p-2 opacity-40'
            >
              <XIcon className='size-5 md:size-6' />
            </DrawerClose>
          )}
        </DrawerPrimitive.Content>
      </DrawerPortal>
    )
  }
)
DrawerContent.displayName = 'DrawerContent'

const DrawerHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('grid gap-1.5 text-center sm:text-left', className)}
    {...props}
  />
)
DrawerHeader.displayName = 'DrawerHeader'

const DrawerFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('mt-auto flex flex-col gap-2 p-4', className)}
    {...props}
  />
)
DrawerFooter.displayName = 'DrawerFooter'

const DrawerTitle: React.ForwardRefExoticComponent<
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Title> &
    React.RefAttributes<React.ComponentRef<typeof DrawerPrimitive.Title>>
> = React.forwardRef<
  React.ComponentRef<typeof DrawerPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Title
    ref={ref}
    className={cn(
      'text-lg font-semibold leading-none tracking-tight',
      className
    )}
    {...props}
  />
))
DrawerTitle.displayName = DrawerPrimitive.Title.displayName

const DrawerDescription: React.ForwardRefExoticComponent<
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Description> &
    React.RefAttributes<React.ComponentRef<typeof DrawerPrimitive.Description>>
> = React.forwardRef<
  React.ComponentRef<typeof DrawerPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Description
    ref={ref}
    className={cn('text-sm opacity-60', className)}
    {...props}
  />
))
DrawerDescription.displayName = DrawerPrimitive.Description.displayName

export {
  Drawer,
  DrawerPortal,
  DrawerOverlay,
  DrawerTrigger,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerFooter,
  DrawerTitle,
  DrawerDescription,
}
